import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';
import { Helmet } from 'react-helmet';
import { Alert, Button, Col, Container, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { fi } from 'date-fns/locale';
import {
  ActionButtons,
  addStatusToast,
  Loader,
  SectionHeader,
  VerticalTabPane,
  VerticalTabs,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  getSeasonRatesAsync,
  SeasonRateDetails,
  selectSeasonRateItems,
  updateSeasonRateAsync,
} from './seasonRatesSlice';
import SeasonRateWeekdayCheckInCheckOutRestrictions from '../rateRules/checkInCheckOutRestriction/SeasonRateWeekdayCheckInCheckOutRestrictions';
import SeasonRateMinimumLengthOfStay from '../rateRules/minimumLengthOfStay/SeasonRateMinimumLengthOfStay';
import SeasonRatePreparationRestriction from '../rateRules/preparationRestriction/SeasonRatePreparationRestriction';
import SeasonRateShortStay from '../rateRules/shortStay/SeasonRateShortStay';
import SeasonRatePriceChange from '../rateRules/priceChange/SeasonRatePriceChange';
import SeasonRateLastMinute from '../rateRules/lastMinute/SeasonRateLastMinute';
import SeasonRateWeekdayMinimumStayRestrictions from '../rateRules/weekdayMinimumStay/SeasonRateWeekdayMinimumStayRestrictions';
import SeasonRateWeekdayPriceChangeRules from '../rateRules/weekdayPriceChange/SeasonRateWeekdayPriceChangeRules';
import SeasonRateWeekdayCheckInCheckOutTimes from '../rateRules/weekdayCheckInCheckOutTime/SeasonRateWeekdayCheckInCheckOutTimes';
import {
  PERMISSIONS,
  SEASON_RATE_COLORS,
  SEASON_RATE_TYPE,
} from '../../common/constants';
import { checkPermission } from '../auth/authSlice';
import SeasonRateRuleReleaseInterval from '../rateRules/releaseInterval/SeasonRateReleaseInterval';
import SeasonRateAddonServiceRules from '../rateRules/addonServiceRules/SeasonRateAddonServiceRules';
import { clearInputErrors, selectInputErrors } from '../errors/errorsSlice';
import InputErrorsDisplay from '../errors/InputErrorsDisplay';
import { getSeasonRatePriceChangeRuleAsync } from '../rateRules/seasonRateRulesSlice';

function UpdateSeasonRate({
  writePermissions,
}: {
  writePermissions?: Array<PERMISSIONS>;
}) {
  const location = useLocation();
  const params = useParams();
  const dispatch = useAppDispatch();
  const parsedQuery = queryString.parse(location.search.replace('?', ''));
  const seasonRateItems = useAppSelector(selectSeasonRateItems);
  const inputErrors = useAppSelector(selectInputErrors);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [accommodationId, setAccommodationId] = useState<string>('');
  const [selectedAccommodationName, setSelectedAccommodationName] =
    useState<string>('');
  const [seasonRateId, setSeasonRateId] = useState<string>('');
  const [seasonRate, setSeasonRate] = useState(null);
  const hasWriteAccess = useAppSelector(checkPermission(writePermissions));

  useEffect(() => {
    dispatch(clearInputErrors());
  }, [dispatch]);

  useEffect(() => {
    if (params.accommodationId && parsedQuery.accommodationId !== '') {
      setAccommodationId(params.accommodationId);
    }

    if (params.seasonRateId && parsedQuery.seasonRateId !== '') {
      setSeasonRateId(params.seasonRateId);
    }

    if (parsedQuery.accommodation && parsedQuery.accommodation !== '') {
      setSelectedAccommodationName(
        decodeURI(parsedQuery.accommodation.toString()),
      );
    }
  }, [params]);

  useEffect(() => {
    if (accommodationId !== '' && seasonRate) {
      setIsLoading(false);
    }
  }, [accommodationId, seasonRate]);

  useEffect(() => {
    if (accommodationId !== '') {
      dispatch(getSeasonRatesAsync({ accommodationId }));
    }
  }, [accommodationId]);

  const Tabs: string[] = [
    'Perustiedot ja hinnat',
    'Kohteen varoaika',
    'Varauksen minimikesto',
    'Sisään- ja uloskirjautumisrajoitukset viikonpäiville',
    'Lyhyen varauksen hinnanmuutos',
    'Hintakauden yleinen hinnan muutos',
    'Viime hetken varauksen hinnan muutos',
    'Varauksen vähimmäiskesto viikonpäiville',
    'Viikonpäiväkohtainen hinnoittelu',
    'Viikonpäiväkohtaiset sisään- ja uloskirjautumisajat',
    'Varattavissa rajoituksetta',
    'Lisäpalvelusäännöt',
  ];

  const [submitted, setSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(Tabs[0]);

  useEffect(() => {
    if (seasonRateId !== '') {
      const getSeasonRateById = _id =>
        seasonRateItems.find(({ id }) => id === seasonRateId);
      if (getSeasonRateById) {
        const seasonRateData: SeasonRateDetails | undefined =
          seasonRateItems.find(({ id }) => id === seasonRateId);

        if (seasonRateData) {
          const newSeasonRateData: SeasonRateDetails = {
            ...seasonRateData,
            ratePrices:
              seasonRateData.ratePrices?.map(price => ({
                ...price,
                dailyPrice:
                  price.discountedPrice && price.discountedPrice > 0
                    ? price.discountedPrice
                    : price.dailyPrice,
              })) ?? [],
            priceForAdditionalDay:
              seasonRateData.discountedPriceForAdditionalDay &&
              seasonRateData.discountedPriceForAdditionalDay > 0
                ? seasonRateData.discountedPriceForAdditionalDay
                : seasonRateData.priceForAdditionalDay,
          };

          setSeasonRate(newSeasonRateData);
          setSubmitted(false);
        } else {
          setSeasonRate(seasonRateData);
          setSubmitted(false);
        }
      }
    }
  }, [seasonRateId, seasonRateItems]);

  const handleInputChange = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const newSeasonRate = { ...seasonRate };
    let pointer = newSeasonRate;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setSeasonRate(newSeasonRate);
  };

  const handleSelectChange = (selectedOption, name) => {
    setSeasonRate(prevState => ({
      ...prevState,
      [name]: selectedOption.value,
    }));
  };

  const handleDateChange = (field: string, value: string | Date) => {
    setSeasonRate(prevState => ({
      ...prevState,
      [field]: value instanceof Date ? format(value, 'yyyy-MM-dd') : value,
    }));
  };

  const handleRatePrices = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const { duration } = event.target.dataset;
    const textValue = event.target.value;
    const newSeasonRate = { ...seasonRate };

    newSeasonRate[finalProp] = seasonRate[finalProp].map(item => {
      if (item.duration.toString() === duration.toString()) {
        return { ...item, dailyPrice: textValue };
      }
      return item;
    });
    setSeasonRate(newSeasonRate);
  };

  const onSubmit = async () => {
    const sanitizedSeasonRate = {
      ...seasonRate,
      priceForAdditionalDay:
        seasonRate.priceForAdditionalDay !== ''
          ? seasonRate.priceForAdditionalDay
          : null,
      commissionPercent:
        seasonRate.commissionPercent !== ''
          ? seasonRate.commissionPercent
          : null,
      ratePrices: seasonRate.ratePrices.map(item => ({
        ...item,
        dailyPrice: item.dailyPrice !== '' ? item.dailyPrice : 0,
      })),
    };

    const actionResult = await dispatch(
      updateSeasonRateAsync(sanitizedSeasonRate),
    );
    if (updateSeasonRateAsync.fulfilled.match(actionResult)) {
      dispatch(
        addStatusToast({
          Message: `Tiedot on tallennettu.`,
        }),
      );
      setSubmitted(true);
      dispatch(clearInputErrors());
      dispatch(getSeasonRatesAsync({ accommodationId }));
      dispatch(getSeasonRatePriceChangeRuleAsync(seasonRateId));
    }
    setSubmitted(true);
  };

  const renderRatePriceItems = seasonRate
    ? seasonRate.ratePrices?.map(item => (
        <Col key={item.duration}>
          <Form.Group className="mb-24">
            <Form.Label>{item.duration} vrk</Form.Label>
            <Form.Control
              type="text"
              name="ratePrices"
              data-duration={item.duration}
              onChange={handleRatePrices}
              value={item.dailyPrice}
              readOnly={!hasWriteAccess}
            />
            <Form.Text muted>Ostohinta: {item.dailyPurchasePrice}</Form.Text>
          </Form.Group>
        </Col>
      ))
    : null;

  const seasonRateColorOptions = SEASON_RATE_COLORS.map(color => {
    return {
      value: color.id,
      label: color.name,
    };
  });

  return (
    <>
      <Helmet>
        <title>Hinnoittelu - Muokkaa sesonkihintakautta</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink={`/rates/seasonrates/${accommodationId}?accommodation=${selectedAccommodationName}`}
          Breadcrumbs={[
            'Hinnoittelu',
            'Sesonkihintakaudet',
            decodeURI(selectedAccommodationName),
          ]}
          Title={`Muokkaa sesonkihintakautta: ${
            seasonRate ? seasonRate.seasonName : ''
          }`}
        />

        <Loader IsLoading={isLoading} />

        {!isLoading && seasonRate && (
          <div className="mt-48">
            <VerticalTabs
              ActiveTab={activeTab}
              ChangeTab={setActiveTab}
              TabContainerId="updateSeasonRateTabs"
              Tabs={Tabs}
            >
              <VerticalTabPane IsActive={activeTab === Tabs[0]} Name={Tabs[0]}>
                <h4 className="h5 mb-24">
                  Hintakauden nimi ja voimassaoloaika
                </h4>
                <Form.Group className="mb-24">
                  <Form.Label>Hintakauden nimi</Form.Label>
                  <Form.Control
                    type="text"
                    name="seasonName"
                    placeholder="Anna hintakauden nimi"
                    onChange={handleInputChange}
                    value={seasonRate.seasonName}
                    required
                    readOnly={!hasWriteAccess}
                  />
                </Form.Group>
                <Form.Group className="mb-24">
                  <Form.Label>Hintakausi alkaa</Form.Label>
                  <div className="w-100 position-relative">
                    <DatePicker
                      locale={fi}
                      id="startDate"
                      name="startDate"
                      dateFormat="dd.MM.yyyy"
                      selected={
                        seasonRate?.startDate
                          ? new Date(seasonRate.startDate)
                          : null
                      }
                      onChange={date => handleDateChange('startDate', date)}
                      className="form-control"
                      readOnly={!hasWriteAccess}
                    />
                    <Button
                      variant="link"
                      className="pe-none position-absolute end-0 top-0"
                      id="button-addon2"
                    >
                      <span className="bi bi-calendar2-event" />
                    </Button>
                  </div>
                </Form.Group>
                <Form.Group className="mb-24">
                  <Form.Label>Hintakausi päättyy</Form.Label>
                  <div className="w-100 position-relative">
                    <DatePicker
                      locale={fi}
                      id="endDate"
                      name="endDate"
                      dateFormat="dd.MM.yyyy"
                      selected={
                        seasonRate?.endDate
                          ? new Date(seasonRate.endDate)
                          : null
                      }
                      onChange={date => handleDateChange('endDate', date)}
                      className="form-control"
                    />
                    <Button
                      variant="link"
                      className="pe-none position-absolute end-0 top-0"
                      id="button-addon2"
                    >
                      <span className="bi bi-calendar2-event" />
                    </Button>
                  </div>
                </Form.Group>
                {seasonRate.seasonRateType === SEASON_RATE_TYPE.Normal && (
                  <Form.Group className="mb-24">
                    <Form.Label>Hintakauden väri</Form.Label>
                    <Select
                      inputId="seasonRateColorId"
                      name="seasonRateColorId"
                      placeholder="Valitse väri..."
                      isClearable
                      options={seasonRateColorOptions}
                      onChange={option =>
                        handleSelectChange(option, 'seasonRateColorId')
                      }
                      value={seasonRateColorOptions.filter(
                        option => option.value === seasonRate.seasonRateColorId,
                      )}
                      classNames={{
                        control: state =>
                          'form-control form-control-react-select',
                      }}
                      isDisabled={!hasWriteAccess}
                    />
                  </Form.Group>
                )}

                <hr className="hr-light mb-32" />

                <h4 className="h5 mb-24">Sisään- ja uloskirjautuminen</h4>
                <Row sm={2}>
                  <Col>
                    <Form.Group className="mb-24">
                      <Form.Label>Sisäänkirjautumisen kellonaika</Form.Label>
                      <Form.Control
                        type="text"
                        name="checkInTime"
                        placeholder="Anna sisäänkirjautumisen kellonaika muodossa HH:mm (esim. 12:30)"
                        onChange={handleInputChange}
                        value={seasonRate.checkInTime}
                        required
                        readOnly={!hasWriteAccess}
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-24">
                      <Form.Label>Uloskirjautumisen kellonaika</Form.Label>
                      <Form.Control
                        type="text"
                        id="checkOutTime"
                        name="checkOutTime"
                        placeholder="Anna uloskirjautumisen kellonaika muodossa HH:mm (esim. 12:30)"
                        onChange={handleInputChange}
                        value={seasonRate.checkOutTime}
                        required
                        readOnly={!hasWriteAccess}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <hr className="hr-light mb-32" />

                <h4 className="h5 mb-24">Hintakauden hinnoittelu</h4>
                <Row sm={2}>
                  <Col>
                    <Form.Group className="mb-24">
                      <Form.Label>Provisioprosentti</Form.Label>
                      <Form.Control
                        type="text"
                        name="commissionPercent"
                        placeholder="Anna provisioprosentti"
                        onChange={handleInputChange}
                        value={seasonRate.commissionPercent}
                        required
                        readOnly={!hasWriteAccess}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <hr className="hr-light mb-32" />

                <h4 className="h5 mb-24">Hinnat keston mukaan</h4>
                <Row sm={2} xl={3}>
                  {renderRatePriceItems}
                </Row>
                <Row sm={2}>
                  <Col>
                    <Form.Group className="mb-24">
                      <Form.Label>Lisävuorokauden hinta</Form.Label>
                      <Form.Control
                        type="text"
                        name="priceForAdditionalDay"
                        placeholder="Lisävuorokauden hinta"
                        onChange={handleInputChange}
                        value={seasonRate.priceForAdditionalDay}
                        required
                        readOnly={!hasWriteAccess}
                      />
                      <Form.Text muted>
                        Ostohinta: {seasonRate.dailyPurchasePrice}
                      </Form.Text>
                      <Form.Text muted>
                        Käytetään päivähintana yli 7 vuorokauden varauksiin.
                        Hinta lisätään jokaiselle vuorokaudelle joka ylittää 7
                        vuorokautta. Esim 10 vuorokauden varauksessa hinta on 7
                        vuorokauden hinta + 3 x lisävuorokauden hinta.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <InputErrorsDisplay inputErrors={inputErrors} />

                <ActionButtons
                  HandleSave={onSubmit}
                  HideSaveButton={!hasWriteAccess}
                  HideCancelButton
                  DeleteButtonText=""
                />
              </VerticalTabPane>
              {seasonRate.id && (
                <>
                  <VerticalTabPane
                    IsActive={activeTab === Tabs[1]}
                    Name={Tabs[1]}
                  >
                    <SeasonRatePreparationRestriction
                      seasonRateId={seasonRate.id}
                      hasWriteAccess={hasWriteAccess}
                    />
                  </VerticalTabPane>

                  <VerticalTabPane
                    IsActive={activeTab === Tabs[2]}
                    Name={Tabs[2]}
                  >
                    <SeasonRateMinimumLengthOfStay
                      seasonRateId={seasonRate.id}
                      hasWriteAccess={hasWriteAccess}
                    />
                  </VerticalTabPane>

                  <VerticalTabPane
                    IsActive={activeTab === Tabs[3]}
                    Name={Tabs[3]}
                  >
                    <SeasonRateWeekdayCheckInCheckOutRestrictions
                      seasonRateId={seasonRate.id}
                      hasWriteAccess={hasWriteAccess}
                    />
                  </VerticalTabPane>

                  <VerticalTabPane
                    IsActive={activeTab === Tabs[4]}
                    Name={Tabs[4]}
                  >
                    <SeasonRateShortStay
                      seasonRateId={seasonRate.id}
                      hasWriteAccess={hasWriteAccess}
                    />
                  </VerticalTabPane>

                  <VerticalTabPane
                    IsActive={activeTab === Tabs[5]}
                    Name={Tabs[5]}
                  >
                    <SeasonRatePriceChange
                      seasonRateId={seasonRate.id}
                      accommodationId={accommodationId}
                      hasWriteAccess={hasWriteAccess}
                    />
                  </VerticalTabPane>

                  <VerticalTabPane
                    IsActive={activeTab === Tabs[6]}
                    Name={Tabs[6]}
                  >
                    <SeasonRateLastMinute
                      seasonRateId={seasonRate.id}
                      hasWriteAccess={hasWriteAccess}
                    />
                  </VerticalTabPane>

                  <VerticalTabPane
                    IsActive={activeTab === Tabs[7]}
                    Name={Tabs[7]}
                  >
                    <SeasonRateWeekdayMinimumStayRestrictions
                      seasonRateId={seasonRate.id}
                      hasWriteAccess={hasWriteAccess}
                    />
                  </VerticalTabPane>

                  <VerticalTabPane
                    IsActive={activeTab === Tabs[8]}
                    Name={Tabs[8]}
                  >
                    <SeasonRateWeekdayPriceChangeRules
                      seasonRateId={seasonRate.id}
                      hasWriteAccess={hasWriteAccess}
                    />
                  </VerticalTabPane>

                  <VerticalTabPane
                    IsActive={activeTab === Tabs[9]}
                    Name={Tabs[9]}
                  >
                    <SeasonRateWeekdayCheckInCheckOutTimes
                      seasonRateId={seasonRate.id}
                      hasWriteAccess={hasWriteAccess}
                    />
                  </VerticalTabPane>

                  <VerticalTabPane
                    IsActive={activeTab === Tabs[10]}
                    Name={Tabs[10]}
                  >
                    <SeasonRateRuleReleaseInterval
                      seasonRateId={seasonRate.id}
                      hasWriteAccess={hasWriteAccess}
                    />
                  </VerticalTabPane>

                  <VerticalTabPane
                    IsActive={activeTab === Tabs[11]}
                    Name={Tabs[11]}
                  >
                    <SeasonRateAddonServiceRules
                      seasonRateId={seasonRate.id}
                      accommodationId={accommodationId}
                      hasWriteAccess={hasWriteAccess}
                    />
                  </VerticalTabPane>
                </>
              )}
            </VerticalTabs>
          </div>
        )}
      </Container>
    </>
  );
}

export default UpdateSeasonRate;
export {};
